import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import { api, fmtUSD, fmtNum, todayISO, CROP_TYPES } from "../lib/api";
import { useYear } from "../context/YearContext";
import { Plus, Warehouse, Truck, Building2, Trash2, Pencil, ArrowRightLeft, Handshake } from "lucide-react";
import { toast } from "sonner";

// Abbreviate common units for tight table displays. Bushels → "bu"; other
// units (bales, tons, cwt, lbs) render as-is, so a hay bin correctly shows
// "500 bales" instead of "500 bu".
const UNIT_ABBR = { bushels: "bu", bushel: "bu" };
const abbr = (u) => UNIT_ABBR[u] || u || "bu";

const blankLoc = { name: "", type: "bin", capacity_bushels: "", address: "", notes: "" };
const blankMove = {
  date: todayISO(), movement_type: "sale", from_location_id: "", to_location_id: "",
  buyer: "", crop: "Corn", quantity: "", unit: "bushels", price_per_unit: "", contract_id: "", notes: "",
  field_id: "", plot_id: "",
};
const blankContract = {
  buyer: "", crop: "Corn", bushels: "", price_per_unit: "", contract_date: todayISO(), delivery_window: "", delivery_date: "", status: "open", notes: "",
};

export default function Inventory() {
  const { year, refreshYears } = useYear();
  const [tab, setTab] = useState("balance");
  const [locations, setLocations] = useState([]);
  const [movements, setMovements] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [balance, setBalance] = useState(null);
  const [fields, setFields] = useState([]);

  const [locOpen, setLocOpen] = useState(false);
  const [locEditing, setLocEditing] = useState(null);
  const [locForm, setLocForm] = useState(blankLoc);

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveForm, setMoveForm] = useState(blankMove);

  const [ctrOpen, setCtrOpen] = useState(false);
  const [ctrEditing, setCtrEditing] = useState(null);
  const [ctrForm, setCtrForm] = useState(blankContract);

  const load = async () => {
    const [l, m, c, b, f] = await Promise.all([
      api.get("/locations"),
      api.get(`/movements?year=${year}`),
      api.get(`/contracts?year=${year}`),
      api.get("/inventory/balance"),
      api.get(`/fields?year=${year}`),
    ]);
    setLocations(l.data);
    setMovements(m.data);
    setContracts(c.data);
    setBalance(b.data);
    setFields(f.data);
  };

  useEffect(() => { load(); }, [year]);

  // Locations
  const onSubmitLoc = async (e) => {
    e.preventDefault();
    const payload = { ...locForm, capacity_bushels: parseFloat(locForm.capacity_bushels) || 0 };
    try {
      if (locEditing) await api.put(`/locations/${locEditing.id}`, payload);
      else await api.post("/locations", payload);
      toast.success(locEditing ? "Location updated" : "Location added");
      setLocOpen(false); setLocEditing(null); setLocForm(blankLoc);
      load();
    } catch { toast.error("Could not save"); }
  };
  const onEditLoc = (l) => {
    setLocEditing(l);
    setLocForm({ name: l.name, type: l.type, capacity_bushels: l.capacity_bushels || "", address: l.address || "", notes: l.notes || "" });
    setLocOpen(true);
  };
  const onDeleteLoc = async (id) => {
    if (!window.confirm("Delete this location? Existing yields tagged here will keep their tag (just won't show under it).")) return;
    await api.delete(`/locations/${id}`);
    load();
  };

  // Movements
  const onSubmitMove = async (e) => {
    e.preventDefault();
    const yearFromDate = new Date(moveForm.date).getFullYear();
    const payload = {
      ...moveForm,
      quantity: parseFloat(moveForm.quantity) || 0,
      price_per_unit: parseFloat(moveForm.price_per_unit) || 0,
      year: yearFromDate || year,
    };
    try {
      await api.post("/movements", payload);
      toast.success(payload.movement_type === "sale" ? "Sale logged (revenue auto-posted)" : "Movement logged");
      setMoveOpen(false); setMoveForm(blankMove);
      refreshYears();
      load();
    } catch { toast.error("Could not save movement"); }
  };
  const onDeleteMove = async (id) => {
    if (!window.confirm("Delete this movement? Linked auto-yield (if any) will also be removed.")) return;
    await api.delete(`/movements/${id}`);
    load();
  };

  // Contracts
  const onSubmitContract = async (e) => {
    e.preventDefault();
    const yearFromDate = new Date(ctrForm.contract_date).getFullYear();
    const payload = {
      ...ctrForm,
      bushels: parseFloat(ctrForm.bushels) || 0,
      price_per_unit: parseFloat(ctrForm.price_per_unit) || 0,
      year: yearFromDate || year,
    };
    try {
      if (ctrEditing) await api.put(`/contracts/${ctrEditing.id}`, payload);
      else await api.post("/contracts", payload);
      toast.success(ctrEditing ? "Contract updated" : "Contract added");
      setCtrOpen(false); setCtrEditing(null); setCtrForm(blankContract);
      refreshYears();
      load();
    } catch { toast.error("Could not save contract"); }
  };
  const onEditContract = (c) => {
    setCtrEditing(c);
    setCtrForm({
      buyer: c.buyer, crop: c.crop, bushels: c.bushels, price_per_unit: c.price_per_unit,
      contract_date: c.contract_date, delivery_window: c.delivery_window || "", delivery_date: c.delivery_date || "",
      status: c.status, notes: c.notes || "",
    });
    setCtrOpen(true);
  };
  const onDeleteContract = async (id) => {
    if (!window.confirm("Delete this contract?")) return;
    await api.delete(`/contracts/${id}`);
    load();
  };

  const locName = (id) => locations.find((l) => l.id === id)?.name || "—";

  return (
    <div>
      <TopBar
        title="Inventory"
        subtitle="Track grain in bins and at the elevator, log movements and sales, and lock in pre-sold contracts."
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-5 border-b border-[#1F1E1B]" data-testid="inv-tabs">
        {[
          { id: "balance", label: "Balance", icon: Warehouse },
          { id: "locations", label: "Bins & Elevators", icon: Building2 },
          { id: "movements", label: "Movements", icon: ArrowRightLeft },
          { id: "contracts", label: "Pre-Sold", icon: Handshake },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border-x border-t -mb-px ${
              tab === id ? "bg-[#1F1E1B] text-white border-[#1F1E1B]" : "bg-white text-[#1F1E1B] border-[#1F1E1B] hover:bg-[#F5F4F0]"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "balance" && (
        <BalanceView balance={balance} onAddMove={() => { setMoveForm({ ...blankMove, date: todayISO() }); setMoveOpen(true); }} />
      )}

      {tab === "locations" && (
        <LocationsView
          locations={locations}
          onAdd={() => { setLocEditing(null); setLocForm(blankLoc); setLocOpen(true); }}
          onEdit={onEditLoc}
          onDelete={onDeleteLoc}
        />
      )}

      {tab === "movements" && (
        <MovementsView
          movements={movements}
          fields={fields}
          locName={locName}
          onAdd={() => {
            if (!locations.length) { toast.error("Add a bin/elevator first."); return; }
            setMoveForm({ ...blankMove, date: todayISO(), from_location_id: locations[0].id });
            setMoveOpen(true);
          }}
          onDelete={onDeleteMove}
        />
      )}

      {tab === "contracts" && (
        <ContractsView
          contracts={contracts}
          onAdd={() => { setCtrEditing(null); setCtrForm({ ...blankContract, contract_date: todayISO() }); setCtrOpen(true); }}
          onEdit={onEditContract}
          onDelete={onDeleteContract}
        />
      )}

      {/* Location modal */}
      {locOpen && (
        <Modal title={locEditing ? "Edit Location" : "Add Bin / Elevator"} onClose={() => setLocOpen(false)}>
          <form onSubmit={onSubmitLoc} className="space-y-4" data-testid="location-form">
            <Field label="Name" required>
              <input data-testid="loc-name-input" required value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} placeholder="North Bin / ADM Decatur" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Type" required>
                <select data-testid="loc-type-select" value={locForm.type} onChange={(e) => setLocForm({ ...locForm, type: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="bin">Bin (on-farm)</option>
                  <option value="elevator">Elevator (third-party)</option>
                </select>
              </Field>
              <Field label="Capacity (bushels)">
                <input data-testid="loc-capacity-input" type="number" step="1" value={locForm.capacity_bushels} onChange={(e) => setLocForm({ ...locForm, capacity_bushels: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <Field label="Address / Description">
              <input data-testid="loc-address-input" value={locForm.address} onChange={(e) => setLocForm({ ...locForm, address: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="Notes">
              <textarea data-testid="loc-notes-input" value={locForm.notes} onChange={(e) => setLocForm({ ...locForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setLocOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="loc-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Location</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Movement modal */}
      {moveOpen && (
        <Modal title="Log Movement" onClose={() => setMoveOpen(false)}>
          <form onSubmit={onSubmitMove} className="space-y-4" data-testid="movement-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Date" required>
                <input data-testid="move-date-input" type="date" required value={moveForm.date} onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Type" required>
                <select data-testid="move-type-select" value={moveForm.movement_type} onChange={(e) => setMoveForm({ ...moveForm, movement_type: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="sale">Sale (auto-posts revenue)</option>
                  <option value="transfer">Transfer between locations</option>
                  <option value="feed">Fed to livestock</option>
                  <option value="loss">Loss / shrink</option>
                </select>
              </Field>
            </div>
            <Field label="From Location" required>
              <select data-testid="move-from-select" required value={moveForm.from_location_id} onChange={(e) => setMoveForm({ ...moveForm, from_location_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                <option value="">Select…</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
              </select>
            </Field>
            {moveForm.movement_type === "transfer" && (
              <Field label="To Location" required>
                <select data-testid="move-to-select" required value={moveForm.to_location_id} onChange={(e) => setMoveForm({ ...moveForm, to_location_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {locations.filter((l) => l.id !== moveForm.from_location_id).map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
                </select>
              </Field>
            )}
            {moveForm.movement_type === "sale" && (
              <>
                <Field label="Buyer">
                  <input data-testid="move-buyer-input" value={moveForm.buyer} onChange={(e) => setMoveForm({ ...moveForm, buyer: e.target.value })} placeholder="ADM, Cargill, local elevator…" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
                </Field>
                <Field label="Fulfilling Contract (optional)">
                  <select data-testid="move-contract-select" value={moveForm.contract_id} onChange={(e) => setMoveForm({ ...moveForm, contract_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                    <option value="">— None —</option>
                    {contracts.filter((c) => ["open", "partial"].includes(c.status) && c.crop === moveForm.crop).map((c) => (
                      <option key={c.id} value={c.id}>{c.buyer} · {c.bushels} bu @ {fmtUSD(c.price_per_unit)} ({c.status})</option>
                    ))}
                  </select>
                </Field>
              </>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Which field? (optional)">
                <select
                  data-testid="move-field-select"
                  value={moveForm.field_id}
                  onChange={(e) => {
                    const f = fields.find((x) => x.id === e.target.value);
                    const firstPlot = f?.plots?.[0];
                    setMoveForm({
                      ...moveForm,
                      field_id: e.target.value,
                      plot_id: firstPlot?.id || "",
                      crop: firstPlot?.crop || f?.crop_type || moveForm.crop,
                    });
                  }}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                >
                  <option value="">— Not tagged —</option>
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </Field>
              {(() => {
                const f = fields.find((x) => x.id === moveForm.field_id);
                const plots = f?.plots || [];
                if (plots.length <= 1) return <div />;
                return (
                  <Field label="Which plot?">
                    <select
                      data-testid="move-plot-select"
                      value={moveForm.plot_id}
                      onChange={(e) => {
                        const p = plots.find((x) => x.id === e.target.value);
                        setMoveForm({ ...moveForm, plot_id: e.target.value, crop: p?.crop || moveForm.crop });
                      }}
                      className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                    >
                      <option value="">Select…</option>
                      {plots.map((p) => <option key={p.id} value={p.id}>{p.crop} ({fmtNum(p.acres, 1)} ac)</option>)}
                    </select>
                  </Field>
                );
              })()}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Crop" required>
                <select data-testid="move-crop-select" value={moveForm.crop} onChange={(e) => setMoveForm({ ...moveForm, crop: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {CROP_TYPES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Bushels" required>
                <input data-testid="move-quantity-input" type="number" step="0.01" required value={moveForm.quantity} onChange={(e) => setMoveForm({ ...moveForm, quantity: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              {moveForm.movement_type === "sale" && (
                <Field label="Price / bu">
                  <input data-testid="move-price-input" type="number" step="0.01" value={moveForm.price_per_unit} onChange={(e) => setMoveForm({ ...moveForm, price_per_unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
                </Field>
              )}
            </div>
            <Field label="Notes">
              <textarea data-testid="move-notes-input" value={moveForm.notes} onChange={(e) => setMoveForm({ ...moveForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setMoveOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="move-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Movement</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Contract modal */}
      {ctrOpen && (
        <Modal title={ctrEditing ? "Edit Contract" : "Pre-Sold Contract"} onClose={() => setCtrOpen(false)}>
          <form onSubmit={onSubmitContract} className="space-y-4" data-testid="contract-form">
            <Field label="Buyer / Elevator" required>
              <input data-testid="ctr-buyer-input" required value={ctrForm.buyer} onChange={(e) => setCtrForm({ ...ctrForm, buyer: e.target.value })} placeholder="Cargill, local co-op…" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Crop" required>
                <select data-testid="ctr-crop-select" value={ctrForm.crop} onChange={(e) => setCtrForm({ ...ctrForm, crop: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {CROP_TYPES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Bushels" required>
                <input data-testid="ctr-bushels-input" type="number" step="1" required value={ctrForm.bushels} onChange={(e) => setCtrForm({ ...ctrForm, bushels: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Price / bu" required>
                <input data-testid="ctr-price-input" type="number" step="0.01" required value={ctrForm.price_per_unit} onChange={(e) => setCtrForm({ ...ctrForm, price_per_unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Contract Date" required>
                <input data-testid="ctr-date-input" type="date" required value={ctrForm.contract_date} onChange={(e) => setCtrForm({ ...ctrForm, contract_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Delivery Window">
                <input data-testid="ctr-window-input" value={ctrForm.delivery_window} onChange={(e) => setCtrForm({ ...ctrForm, delivery_window: e.target.value })} placeholder="Oct–Dec 2026" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Delivery Due Date (push reminders 14 / 7 / 0 days out)">
              <input data-testid="ctr-delivery-date-input" type="date" value={ctrForm.delivery_date} onChange={(e) => setCtrForm({ ...ctrForm, delivery_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="Status">
              <select data-testid="ctr-status-select" value={ctrForm.status} onChange={(e) => setCtrForm({ ...ctrForm, status: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                <option value="open">Open</option>
                <option value="partial">Partially delivered</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Field>
            <Field label="Notes">
              <textarea data-testid="ctr-notes-input" value={ctrForm.notes} onChange={(e) => setCtrForm({ ...ctrForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setCtrOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="ctr-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Contract</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function BalanceView({ balance, onAddMove }) {
  if (!balance) return null;
  return (
    <div data-testid="balance-view">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="bg-[#1A4135] text-white p-4 border border-[#1A4135] md:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C8D5CE]">Grain in Storage</div>
          {(balance.grand_totals_by_unit || []).length > 0 ? (
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              {balance.grand_totals_by_unit.map((t) => (
                <div key={t.unit} className="font-display text-3xl md:text-4xl font-black tabular">
                  {fmtNum(t.quantity, 0)} <span className="text-base font-normal text-[#C8D5CE]">{t.unit}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="font-display text-4xl font-black tabular">0 <span className="text-base font-normal text-[#C8D5CE]">bushels</span></div>
          )}
        </div>
        <button onClick={onAddMove} data-testid="add-movement-btn" className="bg-[#BC4727] text-white p-4 border border-[#BC4727] text-left hover:bg-[#9D391E]">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F8D5C8]">Quick action</div>
          <div className="font-display text-2xl font-black flex items-center gap-2 mt-1"><Truck className="w-6 h-6" /> Log a load</div>
        </button>
      </div>

      {balance.by_crop.length > 0 && (
        <div className="bg-white border border-[#1F1E1B] mb-5 overflow-x-auto" data-testid="balance-by-crop">
          <div className="p-4 border-b border-[#1F1E1B]">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Totals</div>
            <h3 className="font-display text-lg font-bold">By Crop</h3>
          </div>
          <table className="ledger-table">
            <thead>
              <tr><th>Crop</th><th className="num">In Storage</th><th className="num">Pre-Sold (open)</th><th className="num">Free to Sell</th></tr>
            </thead>
            <tbody>
              {balance.by_crop.map((c) => {
                const free = c.bushels - c.contracted_outstanding;
                const negStorage = c.bushels < 0;
                const u = abbr(c.unit);
                return (
                  <tr key={`${c.crop}-${c.unit}`}>
                    <td className="font-semibold">{c.crop} <span className="text-[10px] text-[#66645E] font-normal">({c.unit || "bushels"})</span></td>
                    <td className={`num font-bold ${negStorage ? "text-[#BC4727]" : ""}`}>{fmtNum(c.bushels, 0)} {u}{negStorage ? " ⚠" : ""}</td>
                    <td className="num text-[#BC4727]">{c.contracted_outstanding ? `${fmtNum(c.contracted_outstanding, 0)} ${u}` : "—"}</td>
                    <td className={`num font-bold ${free >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>{fmtNum(free, 0)} {u}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {balance.by_location.length === 0 ? (
        <EmptyState icon={Warehouse} title="No locations yet" hint="Add bins and elevators in the Bins & Elevators tab to start tracking inventory." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {balance.by_location.map((b) => (
            <div key={b.location.id} className={`bg-white border p-4 ${b.negative ? "border-[#BC4727] border-2" : "border-[#1F1E1B]"}`} data-testid={`balance-loc-${b.location.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="stamp">{b.location.type}</span>
                    {b.negative && <span className="stamp stamp-red" data-testid={`oversold-${b.location.id}`}>Oversold</span>}
                  </div>
                  <h3 className="font-display text-lg font-bold mt-2">{b.location.name}</h3>
                  {b.location.address && <div className="text-xs text-[#66645E]">{b.location.address}</div>}
                </div>
                <div className="text-right">
                  {(b.totals_by_unit || []).length > 1 ? (
                    <div className="space-y-0.5">
                      {b.totals_by_unit.map((t) => (
                        <div key={t.unit}>
                          <span className={`font-display text-xl font-black tabular ${b.negative ? "text-[#BC4727]" : ""}`}>{fmtNum(t.quantity, 0)}</span>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#66645E] ml-1">{t.unit}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className={`font-display text-2xl font-black tabular ${b.negative ? "text-[#BC4727]" : ""}`}>{fmtNum(b.total_bushels, 0)}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E]">{b.primary_unit || "bushels"}</div>
                    </>
                  )}
                </div>
              </div>
              {b.negative && (
                <div className="text-[11px] text-[#BC4727] bg-[#FBE9E2] border border-[#BC4727] p-2 mt-2">
                  <strong>Heads up:</strong> sales/transfers exceed what was logged into this location. Check your harvest entries or movement quantities.
                </div>
              )}
              {b.fill_pct != null && !b.negative && (
                <div className="my-2">
                  <div className="h-2 bg-[#F5F4F0] border border-[#D2CEC1] overflow-hidden">
                    <div className={`h-full ${b.fill_pct > 95 ? "bg-[#BC4727]" : "bg-[#1A4135]"}`} style={{ width: `${Math.min(b.fill_pct, 100)}%` }} />
                  </div>
                  <div className="text-[10px] text-[#66645E] mt-1">{b.fill_pct}% of {fmtNum(b.location.capacity_bushels, 0)} bu capacity</div>
                </div>
              )}
              {b.crops.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm">
                  {b.crops.map((c) => (
                    <li key={`${c.crop}-${c.unit}`} className="flex justify-between border-b border-dashed border-[#E5E2D6] py-1">
                      <span>{c.crop}</span>
                      <span className={`font-bold tabular ${c.bushels < 0 ? "text-[#BC4727]" : ""}`}>{fmtNum(c.bushels, 0)} {abbr(c.unit)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LocationsView({ locations, onAdd, onEdit, onDelete }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-lg font-bold">Bins & Elevators</h3>
        <button data-testid="add-location-btn" onClick={onAdd} className="bg-[#BC4727] text-white px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#9D391E] flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      {locations.length === 0 ? (
        <EmptyState icon={Building2} title="No locations yet" hint="Add a grain bin (on-farm) or an elevator (third-party) to track where each load goes." />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto" data-testid="locations-table">
          <table className="ledger-table">
            <thead><tr><th>Name</th><th>Type</th><th className="num">Capacity</th><th>Address</th><th style={{ width: 90 }}></th></tr></thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.id} data-testid={`location-row-${l.id}`}>
                  <td className="font-semibold">{l.name}</td>
                  <td><span className="stamp">{l.type}</span></td>
                  <td className="num">{l.capacity_bushels ? `${fmtNum(l.capacity_bushels, 0)} bu` : "—"}</td>
                  <td className="text-[#66645E]">{l.address || "—"}</td>
                  <td>
                    <div className="flex gap-1.5 justify-end">
                      <button data-testid={`edit-location-${l.id}`} onClick={() => onEdit(l)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                      <button data-testid={`delete-location-${l.id}`} onClick={() => onDelete(l.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MovementsView({ movements, fields, locName, onAdd, onDelete }) {
  const fieldLabel = (m) => {
    if (!m.field_id) return "—";
    const f = fields.find((x) => x.id === m.field_id);
    if (!f) return m.field_name || "—";
    const plot = f.plots?.find((p) => p.id === m.plot_id);
    return plot ? `${f.name} · ${plot.crop}` : f.name;
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-lg font-bold">Movements</h3>
        <button data-testid="add-movement-btn-2" onClick={onAdd} className="bg-[#BC4727] text-white px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#9D391E] flex items-center gap-2">
          <Plus className="w-4 h-4" /> Log
        </button>
      </div>
      {movements.length === 0 ? (
        <EmptyState icon={ArrowRightLeft} title="No movements logged" hint="Log a sale, transfer between bins, livestock feed, or shrink." />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto" data-testid="movements-table">
          <table className="ledger-table">
            <thead>
              <tr><th>Date</th><th>Type</th><th>Crop</th><th>Field / Plot</th><th className="num">Bushels</th><th>From</th><th>To / Buyer</th><th className="num">Revenue</th><th style={{ width: 60 }}></th></tr>
            </thead>
            <tbody>
              {movements.map((m) => (
                <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                  <td className="tabular">{m.date}</td>
                  <td><span className={`stamp ${m.movement_type === "sale" ? "stamp-green" : ""}`}>{m.movement_type}</span></td>
                  <td>{m.crop}</td>
                  <td className="text-[#66645E]" data-testid={`movement-field-${m.id}`}>{fieldLabel(m)}</td>
                  <td className="num font-bold">{fmtNum(m.quantity, 0)} bu</td>
                  <td>{m.from_location_id ? locName(m.from_location_id) : "—"}</td>
                  <td>{m.to_location_id ? locName(m.to_location_id) : (m.buyer || "—")}</td>
                  <td className="num font-bold">{m.movement_type === "sale" && m.price_per_unit ? fmtUSD(m.quantity * m.price_per_unit) : "—"}</td>
                  <td>
                    <button data-testid={`delete-movement-${m.id}`} onClick={() => onDelete(m.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContractsView({ contracts, onAdd, onEdit, onDelete }) {
  const totalValue = contracts.filter((c) => c.status !== "cancelled").reduce((s, c) => s + c.bushels * c.price_per_unit, 0);
  const openValue = contracts.filter((c) => ["open", "partial"].includes(c.status)).reduce((s, c) => s + c.bushels * c.price_per_unit, 0);
  const statusColor = (s) => s === "open" ? "stamp-green" : s === "partial" ? "" : s === "delivered" ? "" : "stamp-red";
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Open Pre-Sold Value</div>
          <div className="font-display text-2xl font-black tabular">{fmtUSD(openValue)}</div>
        </div>
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">All Contracts (excl. cancelled)</div>
          <div className="font-display text-2xl font-black tabular">{fmtUSD(totalValue)}</div>
        </div>
        <button data-testid="add-contract-btn" onClick={onAdd} className="bg-[#BC4727] text-white p-4 border border-[#BC4727] text-left hover:bg-[#9D391E]">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F8D5C8]">Quick action</div>
          <div className="font-display text-2xl font-black flex items-center gap-2 mt-1"><Handshake className="w-6 h-6" /> Lock in price</div>
        </button>
      </div>
      {contracts.length === 0 ? (
        <EmptyState icon={Handshake} title="No contracts yet" hint="Pre-sell future production at a fixed price. We'll show outstanding bushels against your inventory." />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto" data-testid="contracts-table">
          <table className="ledger-table">
            <thead>
              <tr><th>Date</th><th>Buyer</th><th>Crop</th><th className="num">Bushels</th><th className="num">Price</th><th className="num">Value</th><th>Window</th><th>Status</th><th style={{ width: 90 }}></th></tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} data-testid={`contract-row-${c.id}`}>
                  <td className="tabular">{c.contract_date}</td>
                  <td className="font-semibold">{c.buyer}</td>
                  <td>{c.crop}</td>
                  <td className="num font-bold">{fmtNum(c.bushels, 0)} bu</td>
                  <td className="num">{fmtUSD(c.price_per_unit)}</td>
                  <td className="num font-bold">{fmtUSD(c.bushels * c.price_per_unit)}</td>
                  <td className="text-[#66645E]" data-testid={`contract-delivery-${c.id}`}>{c.delivery_window || (c.delivery_date ? "" : "—")}{c.delivery_date && <span className="block text-xs font-semibold text-[#1F2A47]">Due {c.delivery_date}</span>}</td>
                  <td><span className={`stamp ${statusColor(c.status)}`}>{c.status}</span></td>
                  <td>
                    <div className="flex gap-1.5 justify-end">
                      <button data-testid={`edit-contract-${c.id}`} onClick={() => onEdit(c)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                      <button data-testid={`delete-contract-${c.id}`} onClick={() => onDelete(c.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
